import { useContext, useEffect, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';

import { pluralize } from '../../utils';
import { buildSearchResults, splitOnSearchTerm } from '../../utils/json-search';
import Button from './base/Button';
import Text from './base/Text';
import DropdownMenu, { DropdownMenuOption } from './DropdownMenu';
import { Div } from './helpers/StyledUtils';
import { useCopyToClipboard } from '../../utils/copy';
import { FiltersContext } from './Filters/Filters';
import { StyledDropdownWrapper } from './Dropdown';
import { useToasts } from './Toast';
import { TableContext } from '../contexts/TableContext';
import Icon from './base/Icon';
import Tooltip from './base/Tooltip';

/** Config for JSON browser context menu. Defined by the parent so the browser stays generic. */
export interface ContextMenuConfig {
  /** Key in filters_context.values to read/write for "Add to filter" / "Remove from filter". */
  filter_field_key?: string;
  /** Prefix for table column paths (e.g. "body", "headers") for "Add to table" / "Remove from table". */
  table_column_prefix?: string;
}

export function insertPathEllipsis(text: string, max_length: number) {
  if (text.length <= max_length) return text;
  const ellipsis_length = 3;
  const initial_length = 1;
  const side_length = max_length - ellipsis_length - initial_length;
  return text.slice(0, initial_length) + '...' + text.slice(text.length - side_length);
}

type JSONPrimitive = string | number | boolean | null;

export type JSONValue =
  | JSONPrimitive
  | (JSONPrimitive | JSONValue)[]
  | { [key: string]: JSONPrimitive | JSONValue };

const isPrimitive = (value: JSONValue) => {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === null
  );
};

export const DynamicColumnHeader: React.FC<{
  full_path: string;
  width?: number;
}> = ({ full_path, width = 200 }) => {
  const filters_context = useContext(FiltersContext);
  const table_context = useContext(TableContext);

  const [type, ...path] = full_path.split('.');
  const field_type = type === 'body' ? 'request_body' : 'request_headers';

  // Check if there's an active filter for this field
  let filtered_value = null;
  try {
    const current_filter = JSON.parse(filters_context.values?.[field_type] || '{}');
    filtered_value = getNestedValue(current_filter, path);
  } catch (error) {
    // No filter or invalid JSON
  }

  const is_filtered = filtered_value !== undefined && filtered_value !== null;

  return (
    <Div flex={{ align: 'center', justify: 'space-between', gap: 1 }} w={100}>
      <Div flex={{ align: 'center', gap: 1 }}>
        <Tooltip
          delay={500}
          tooltip={
            <Text mono muted size="xs">
              {full_path}
            </Text>
          }
          side="top">
          <Text mono muted size="xs" style={{ cursor: 'grab', pointerEvents: 'none' }}>
            {insertPathEllipsis(full_path, (width - (is_filtered ? 45 : 20)) / 8)}
          </Text>
        </Tooltip>
        {is_filtered && (
          <Div
            flex={{ align: 'center', justify: 'center' }}
            style={{ cursor: 'grab', pointerEvents: 'none' }}>
            <Tooltip
              side="top"
              delay={500}
              element={
                <Div style={{ maxHeight: '300px', width: '300px', overflow: 'auto' }} p={{ x: 1 }}>
                  <Text size="xs" semi muted m={{ b: 1 }}>
                    Filter value
                  </Text>
                  <JSONBrowser json={{ [`${path[path.length - 1]}`]: filtered_value }} compact />
                </Div>
              }>
              <Icon icon="filter_list" />
            </Tooltip>
          </Div>
        )}
      </Div>
      <Button
        ghost
        icon="close"
        small
        onClick={(e) => {
          e.stopPropagation();
          table_context.removeDynamicColumn(full_path);
        }}
      />
    </Div>
  );
};

const StyledKey = styled.span<{ compact: boolean }>`
  font-size: ${({ theme, compact }) => theme.pxToRem(compact ? 12 : 13)};
  font-family:
    JetBrains Mono,
    monospace;
`;

const StyledValue = styled.span<{ type: JSONPrimitive; compact: boolean }>(
  ({ theme, type, compact }) => css`
    color: ${theme.colors.on.code[String(type)]};
    font-size: ${theme.pxToRem(compact ? 12 : 13)};
    font-family:
      JetBrains Mono,
      monospace;
    display: inline-block;
    max-width: 100%;
    word-break: break-all;
  `,
);
const StyledHint = styled.span<{
  count?: number;
  disable_selection?: boolean;
  closed?: boolean;
  compact: boolean;
}>(
  ({ theme, count, disable_selection, closed, compact }) => css`
    color: ${theme.colors.on.neutral.primary_neutral};
    margin: 0;
    border: none;
    padding: ${theme.spacing(0.25)};
    background-color: transparent;
    font-size: ${theme.pxToRem(compact ? 12 : 13)};

    &:focus-visible {
      outline: 2px solid ${theme.colors.outline.focus.primary};
      outline-offset: 1px;
    }

    ${disable_selection
      ? css`
          user-select: none;
        `
      : css`
          padding: ${theme.spacing(0.25)};
          border-radius: ${theme.radius.small};
          &:hover {
            background-color: ${theme.colors.surface.base.variant_surface};
          }
        `}
    ${count !== undefined &&
    css`
      &::after {
        content: ${count === 0
          ? '"Empty"'
          : `"${count} ${pluralize(count, 'item')} ${closed ? '↓' : '↑'}"`};
        display: inline-block;
        user-select: none;
        margin-left: ${theme.spacing(1)};
        font-size: ${theme.pxToRem(compact ? 11 : 12)};
        color: ${theme.colors.on.neutral.secondary_neutral};
        font-family:
          JetBrains Mono,
          monospace;
      }
    `}
  `,
);

const StyledCollapseButton = styled(Button)<{ compact: boolean }>(
  ({ theme, compact }) => css`
    position: absolute;
    top: 0;
    right: 0;

    color: ${theme.colors.on.neutral.primary_neutral};
    border: none;
    background-color: transparent;
    font-size: ${theme.pxToRem(compact ? 11 : 12)};
    padding: ${theme.spacing(0.5)} ${theme.spacing(2)};
    margin: ${theme.spacing(-0.5)} ${theme.spacing(-2)};
    font-weight: normal;

    &:focus {
      outline: none;
      background-color: transparent;
    }

    &:focus-visible {
      box-shadow: none;
      outline: 2px solid ${theme.colors.outline.focus.primary};
      outline-offset: 1px;
    }
  `,
);

const StyledLine = styled.span<{ depth: number }>(
  ({ theme, depth }) => css`
    display: block;
    padding-left: ${theme.spacing((0.5 * depth + 2) as any)};
    position: relative;
    background-color: transparent;
    border: none;
    outline: none;
    text-align: left;
    overflow-wrap: anywhere;
  `,
);

const StyledLineContentHover = styled.span<{ active: boolean }>(
  ({ theme, active }) => css`
    position: relative;
    display: inline-block;
    border-radius: ${theme.radius.small};
    &:hover {
      background-color: ${theme.colors.surface.base.variant_surface};
    }
    ${active &&
    css`
      background-color: ${theme.colors.surface.base.variant_surface};
    `}
    cursor: pointer;
  `,
);

const StyledKeyContentHover = styled.span<{ active: boolean }>(
  ({ theme, active }) => css`
    position: relative;
    display: inline;
    border-radius: ${theme.radius.small};
    &:hover {
      background-color: ${theme.colors.surface.base.variant_surface};
    }
    ${active &&
    css`
      background-color: ${theme.colors.surface.base.variant_surface};
    `}
    cursor: pointer;
  `,
);

const setNestedValue = (obj: any, path: (string | number)[], value: any): any => {
  if (path.length === 0) return value;
  if (path.length === 1) {
    if (Array.isArray(obj)) {
      const new_array = [...obj];
      new_array[path[0] as number] = value;
      return new_array;
    }
    return { ...obj, [path[0]]: value };
  }
  const [first, ...rest] = path;
  const next_key = rest[0];
  const should_be_array = typeof next_key === 'number';

  if (Array.isArray(obj)) {
    const new_array = [...obj];
    const current_value = new_array[first as number];
    new_array[first as number] = setNestedValue(
      current_value !== undefined ? current_value : should_be_array ? [] : {},
      rest,
      value,
    );
    return new_array;
  }

  const current_value = obj[first];
  return {
    ...obj,
    [first]: setNestedValue(
      current_value !== undefined ? current_value : should_be_array ? [] : {},
      rest,
      value,
    ),
  };
};

const removeNestedValue = (obj: any, path: (string | number)[]): any => {
  if (path.length === 0) return obj;
  if (path.length === 1) {
    if (Array.isArray(obj)) {
      return obj.filter((_, i) => i !== path[0]);
    }
    const { [path[0]]: _, ...rest } = obj;
    return rest;
  }
  const [first, ...rest] = path;
  if (!obj[first]) return obj;
  const updated_nested = removeNestedValue(obj[first], rest);
  const is_empty = Array.isArray(updated_nested)
    ? updated_nested.length === 0
    : Object.keys(updated_nested).length === 0;

  if (is_empty) {
    if (Array.isArray(obj)) {
      return obj.filter((_, i) => i !== first);
    }
    const { [first]: _, ...remaining } = obj;
    return remaining;
  }

  if (Array.isArray(obj)) {
    const new_array = [...obj];
    new_array[first as number] = updated_nested;
    return new_array;
  }

  return {
    ...obj,
    [first]: updated_nested,
  };
};

const getNestedValue = (obj: any, path: (string | number)[]): any => {
  if (path.length === 0) return obj;
  const [first, ...rest] = path;
  if (!obj || typeof obj !== 'object') return undefined;
  return getNestedValue(obj[first], rest);
};

export const useCodeMenuOptions = (
  json_key: string,
  json_value: JSONValue,
  config: ContextMenuConfig | null,
  path: (string | number)[],
  appended_header?: boolean,
) => {
  const copyToClipboard = useCopyToClipboard();
  const filters_context = useContext(FiltersContext);
  const table_context = useContext(TableContext);
  const { addToast } = useToasts();
  const [filter_added, setFilterAdded] = useState(false);
  const [table_added, setTableAdded] = useState(false);

  const filter_field_key = config?.filter_field_key;
  const table_column_prefix = config?.table_column_prefix;

  useEffect(() => {
    try {
      if (appended_header || !filter_field_key) return;
      const current_body = JSON.parse(filters_context.values?.[filter_field_key] || '{}');
      const normalized_path = path.map((p) => (typeof p === 'number' ? 0 : p));
      const existing_value = getNestedValue(current_body, normalized_path);
      setFilterAdded(JSON.stringify(existing_value) === JSON.stringify(json_value));
    } catch (error) {
      setFilterAdded(false);
    }
  }, [filters_context.values, filter_field_key, path, json_value, appended_header]);

  useEffect(() => {
    if (appended_header || !table_column_prefix) {
      setTableAdded(false);
      return;
    }
    const field_path = `${table_column_prefix}.${path.map(String).join('.')}`;
    // Check if any column matches this field_path (ignoring width)
    const is_active = table_context.dynamic_columns.some((col) => {
      const col_field_path = col.split(':')[0];
      return col_field_path === field_path;
    });
    setTableAdded(is_active);
  }, [table_context.dynamic_columns, path, table_column_prefix, appended_header]);

  const addToFilter = () => {
    if (appended_header || !filter_field_key) return;

    const current_filter = JSON.parse(filters_context.values?.[filter_field_key] || '{}');
    // Normalize array indices to 0 so the filter doesn't create sparse arrays with nulls
    const normalized_path = path.map((p) => (typeof p === 'number' ? 0 : p));
    const updated_filter = setNestedValue(current_filter, normalized_path, json_value);

    filters_context.formik.setFieldValue(filter_field_key, JSON.stringify(updated_filter, null, 2));
    filters_context.formik.handleSubmit();
    addToast('success', 'Added to filters');
  };

  const removeFromFilter = () => {
    if (appended_header || !filter_field_key) return;

    const current_body = JSON.parse(filters_context.values?.[filter_field_key] || '{}');
    const normalized_path = path.map((p) => (typeof p === 'number' ? 0 : p));
    const updated_body = removeNestedValue(current_body, normalized_path);

    filters_context.formik.setFieldValue(filter_field_key, JSON.stringify(updated_body, null, 2));
    filters_context.formik.handleSubmit();
    addToast('success', 'Removed from filters');
  };

  const addFieldToTable = () => {
    if (!table_column_prefix) return;

    const field_path = `${table_column_prefix}.${path.map(String).join('.')}`;

    // Calculate width based on the value
    let width = 200; // default
    if (json_value !== undefined && json_value !== null) {
      let value_string: string;
      if (typeof json_value === 'object') {
        value_string = JSON.stringify(json_value);
      } else {
        value_string = String(json_value);
      }
      // 8px per character, min 150px, max 600px
      const calculated_width = value_string.length * 8;
      width = Math.max(150, Math.min(600, calculated_width));
    }

    const column_string = `${field_path}:${width}`;
    table_context.addDynamicColumn(column_string);
    addToast('success', 'Added to table');
  };

  const removeFromTable = () => {
    if (!table_column_prefix) return;

    const field_path = `${table_column_prefix}.${path.map(String).join('.')}`;

    // removeDynamicColumn now handles matching by field path
    table_context.removeDynamicColumn(field_path);
    addToast('success', 'Removed from table');
  };

  const show_filter_actions = Boolean(filter_field_key);

  const options = [
    ...(config
      ? [
          ...(show_filter_actions
            ? [
                {
                  label: filter_added ? 'Remove from filter' : 'Add to filter',
                  icon: filter_added ? 'cancel' : 'filter_list',
                  onClick: () => (filter_added ? removeFromFilter() : addToFilter()),
                  selected: filter_added,
                  disabled: appended_header,
                  tooltip: appended_header
                    ? 'Appended headers cannot be added to the filter'
                    : undefined,
                },
              ]
            : []),
          ...(table_column_prefix
            ? [
                {
                  label: table_added ? 'Remove from table' : 'Add field to table',
                  icon: table_added ? 'cancel' : 'add_circle',
                  onClick: () => (table_added ? removeFromTable() : addFieldToTable()),
                  selected: table_added,
                },
              ]
            : []),
        ]
      : []),
    {
      label: `Copy to clipboard`,
      icon: 'copy',
      onClick: () => copyToClipboard(`"${json_key}": ${JSON.stringify(json_value, null, 2)}`),
    },
  ] as DropdownMenuOption[];
  return options;
};

const StyledJSONKeyWrapper = styled.span`
  display: inline-block;
  margin-right: ${({ theme }) => theme.spacing(2)};

  ${StyledDropdownWrapper} {
    display: inline-block;
  }
`;
const JSONKeyWrapper: React.FC<{
  json_key: string;
  json_value: JSONValue;
  context_menu: boolean;
  context_menu_config: ContextMenuConfig | null;
  path: (string | number)[];
  children: React.ReactNode;
}> = ({ json_key, json_value, context_menu, context_menu_config, path, children }) => {
  const options = useCodeMenuOptions(
    json_key,
    json_value,
    context_menu ? context_menu_config : null,
    path,
  );

  return (
    <StyledJSONKeyWrapper>
      <DropdownMenu
        parent_width={{ min: 192, max: 192 }}
        options={options}
        renderToggle={(opened, toggle) => (
          <StyledKeyContentHover active={opened} onClick={() => toggle(true)}>
            {children}
          </StyledKeyContentHover>
        )}
      />
    </StyledJSONKeyWrapper>
  );
};

const JSONLine: React.FC<{
  is_primitive: boolean;
  children: React.ReactNode;
  depth: number;
  json_key: string;
  json_value: JSONValue;
  context_menu: boolean;
  context_menu_config: ContextMenuConfig | null;
  path: (string | number)[];
}> = ({
  is_primitive,
  children,
  depth,
  json_key,
  json_value,
  context_menu,
  context_menu_config,
  path,
}) => {
  const options = useCodeMenuOptions(
    json_key,
    json_value,
    context_menu ? context_menu_config : null,
    path,
  );

  if (!is_primitive) return <StyledLine depth={depth}>{children}</StyledLine>;

  return (
    <StyledLine depth={depth}>
      <DropdownMenu
        parent_width={{ min: 208, max: 208 }}
        options={options}
        renderToggle={(opened, toggle) => (
          <StyledLineContentHover active={opened} onClick={() => toggle(true)}>
            {children}
          </StyledLineContentHover>
        )}
      />
    </StyledLine>
  );
};

const HighlightSearchTerm: React.FC<{
  Component: any;
  value: string;
  compact: boolean;
  search_term: string;
  props?: object;
}> = ({ Component, value, search_term, props, compact }) => {
  const theme = useTheme();
  if (!search_term) {
    return <Component {...(props || {})}>{value}</Component>;
  }
  const parts = splitOnSearchTerm(value, search_term);
  return (
    <>
      {parts.map((p, i) => (
        <Component
          {...(props || {})}
          compact={compact}
          key={i}
          style={
            search_term === p ? { backgroundColor: theme.colors.surface.container.highlight } : {}
          }>
          {p}
        </Component>
      ))}
    </>
  );
};

const PrimitiveComponent: React.FC<{
  value: JSONPrimitive;
  search_term: string;
  compact: boolean;
}> = ({ value, search_term, compact }) => {
  let type = 'string';
  if (typeof value === 'number') {
    type = 'number';
  } else if (typeof value === 'boolean') {
    type = 'boolean';
  } else if (value === null) {
    type = 'null';
  }
  return (
    <HighlightSearchTerm
      compact={compact}
      search_term={search_term}
      Component={StyledValue}
      props={{ type }}
      value={type === 'string' ? `"${value}"` : String(value)}
    />
  );
};

type CollapseStatus = 'collapsed' | 'expanded' | null;

const RecursiveJsonComponent: React.FC<{
  search_term: string;
  search_results: boolean | object | undefined;
  json: JSONValue;
  depth: number;
  compact: boolean;
  context_menu: boolean;
  context_menu_config: ContextMenuConfig | null;
  collapse_status: CollapseStatus;
  setCollapse: (status: CollapseStatus, last_action: 'collapse' | 'expand') => void;
  path?: (string | number)[];
}> = ({
  search_results,
  search_term,
  json,
  depth,
  collapse_status,
  setCollapse,
  compact,
  context_menu,
  context_menu_config = null,
  path = [],
}) => {
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (!isPrimitive(json)) {
      if (collapse_status === 'expanded') {
        setClosed(false);
      } else if (collapse_status === 'collapsed') {
        setClosed(true);
      }
    }
  }, [collapse_status, json]);

  if (isPrimitive(json)) {
    return (
      <PrimitiveComponent
        search_term={search_term}
        value={json as JSONPrimitive}
        compact={compact}
      />
    );
  }

  const is_array = Array.isArray(json);
  const entries: [number | string, JSONValue][] = is_array
    ? json.map((value: JSONValue, i) => [i, value])
    : Object.entries(json as { [key: string]: JSONValue });

  if (entries.length === 0) {
    return (
      <StyledHint compact={compact} count={0}>
        {is_array ? '[ ]' : '{ }'}
      </StyledHint>
    );
  }

  const onClick = (e) => {
    e.stopPropagation();
    if (!isPrimitive(json)) {
      setCollapse(collapse_status, !closed ? 'collapse' : 'expand');
      setClosed((closed) => !closed);
    }
  };

  if (closed) {
    return (
      <StyledHint
        as="button"
        disable_selection
        compact={compact}
        count={entries.length}
        closed={true}
        onClick={onClick}>
        {is_array ? ' [...]' : ' {...}'}
      </StyledHint>
    );
  }

  return (
    <>
      <StyledHint
        as="button"
        compact={compact}
        count={entries.length}
        {...(entries.length > 0
          ? {
              onClick: onClick,
            }
          : {})}>
        {is_array ? '[' : '{'}
      </StyledHint>
      {entries.map(([key, value], i) => {
        if (search_results && !search_results[i]) {
          return null;
        }

        const current_path = [...path, key];

        const is_primitive_value = isPrimitive(value);

        return (
          <JSONLine
            key={key}
            depth={depth + 1}
            json_key={String(key)}
            json_value={value}
            is_primitive={is_primitive_value}
            context_menu={context_menu}
            context_menu_config={context_menu_config}
            path={current_path}>
            {is_array ? (
              <StyledHint compact={compact} disable_selection>
                {key}:{' '}
              </StyledHint>
            ) : is_primitive_value ? (
              <HighlightSearchTerm
                Component={StyledKey}
                compact={compact}
                search_term={search_term}
                value={`"${key}": `}
              />
            ) : (
              <JSONKeyWrapper
                json_key={String(key)}
                json_value={value}
                context_menu={context_menu}
                context_menu_config={context_menu_config}
                path={current_path}>
                <HighlightSearchTerm
                  Component={StyledKey}
                  compact={compact}
                  search_term={search_term}
                  value={`"${key}": `}
                />
              </JSONKeyWrapper>
            )}
            <RecursiveJsonComponent
              context_menu_config={context_menu_config}
              compact={compact}
              search_results={
                !search_results || (!is_array && String(key).indexOf(search_term) >= 0)
                  ? undefined
                  : search_results[i]
              }
              search_term={search_term}
              json={value}
              depth={depth + 1}
              collapse_status={collapse_status}
              setCollapse={setCollapse}
              context_menu={context_menu}
              path={current_path}
            />
            {i !== entries.length - 1 && <StyledHint compact={compact}>,</StyledHint>}
          </JSONLine>
        );
      })}
      <StyledHint compact={compact}>{is_array ? ']' : '}'}</StyledHint>
    </>
  );
};

const JSONBrowser: React.FC<{
  search_term?: string;
  json: JSONValue;
  compact?: boolean;
  context_menu?: boolean;
  context_menu_config?: ContextMenuConfig | null;
}> = ({ search_term, json, compact, context_menu = false, context_menu_config = null }) => {
  const search_results =
    search_term && search_term?.length > 1 ? buildSearchResults(search_term, json) : undefined;
  const [collapse_status, setCollapseStatus] = useState<CollapseStatus>('expanded');
  const [last_action, setLastAction] = useState<'collapse' | 'expand' | null>(null);

  const setCollapse = (status: CollapseStatus, last_action: 'collapse' | 'expand') => {
    setCollapseStatus(null);
    setLastAction(last_action);
  };

  if (search_term && search_term?.length > 1 && !search_results) {
    return <Text muted>No match found.</Text>;
  }

  let collapse_action = 'collapsed';
  if (collapse_status === 'collapsed' || last_action === 'collapse') {
    collapse_action = 'expanded';
  }
  return (
    <Div style={{ position: 'relative' }}>
      <RecursiveJsonComponent
        compact={!!compact}
        search_term={search_term && search_term?.length > 1 ? search_term : ''}
        search_results={search_results}
        json={json}
        depth={0}
        collapse_status={collapse_status}
        setCollapse={setCollapse}
        context_menu={context_menu}
        context_menu_config={context_menu_config}
      />
      {!isPrimitive(json) && (
        <StyledCollapseButton
          compact={!!compact}
          ghost
          m={{ t: 1 }}
          onClick={() => {
            setCollapseStatus(collapse_action !== 'collapsed' ? 'expanded' : 'collapsed');
            setLastAction(collapse_action !== 'collapsed' ? 'expand' : 'collapse');
          }}>
          <Text mono neutral size="s">
            {collapse_action !== 'collapsed' ? 'Expand all ↓' : 'Collapse all ↑'}
          </Text>
        </StyledCollapseButton>
      )}
    </Div>
  );
};

export default JSONBrowser;
